import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, timer, race, interval } from 'rxjs';
import { map, take, takeWhile } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '@environments/environment';
import { ModalPromptService } from '@core/services/modal-prompt.service';
import { ModalPromptModel } from '@shared/interfaces/modal-prompt-model.interface';

@Injectable({
  providedIn: 'root',
})
export class InactivityService implements OnDestroy {
  private readonly subscriptions: Subscription = new Subscription();
  private readonly userActivity = new Subject<void>();
  private inactivityTimerSubscription: Subscription = new Subscription();
  private readonly inactivityTimeLimit =
    Number(environment.inactivityTimeLimitInMinutes) * 60 * 1000;
  private readonly logoutInactivityTime = Number(
    environment.inactivityLogoutTimeInSeconds,
  );

  constructor(
    private readonly _authService: AuthService,
    private readonly _modalPromptService: ModalPromptService,
  ) {
    const activitySub = this.userActivity.subscribe(() => this.resetTimer());
    this.subscriptions.add(activitySub);

    this.resetTimer();
  }

  registerUserActivity() {
    this.userActivity.next();
  }

  private resetTimer() {
    // Unsubscribe from the previous timer if it exists
    if (this.inactivityTimerSubscription) {
      this.inactivityTimerSubscription.unsubscribe();
    }
    // Create a new timer that will trigger the promptLogout after the inactivity limit
    this.inactivityTimerSubscription = timer(
      this.inactivityTimeLimit,
    ).subscribe(() => {
      if (this._authService.isAuthenticated()) {
        this.promptLogout();
      }
    });
    // Add the timer subscription to our composite subscription
    this.subscriptions.add(this.inactivityTimerSubscription);
  }

  promptLogout(): void {
    const structure: ModalPromptModel = {
      title: 'Tu sesión está por expirar.',
      question: '¿Deseas extender tu tiempo de sesión?',
      primaryButtonText: 'Sí, extender sesión',
      warningButtonText: 'No, cerrar sesión',
      countdown: this.logoutInactivityTime,
    };

    // Open the modal immediately with the initial structure
    this._modalPromptService.openModal(structure);

    // Create a countdown observable that emits every second
    const countdown$ = interval(1000).pipe(
      map((elapsed) => 60 - elapsed), // Calculate remaining seconds
      takeWhile((remaining) => remaining >= 0),
    );

    // Subscribe to update the modal's countdown display
    const countdownSubscription = countdown$.subscribe((remaining) => {
      const updatedStructure = { ...structure, countdown: remaining };
      this._modalPromptService.openModal(updatedStructure);
    });
    this.subscriptions.add(countdownSubscription);

    // Observable that fires after 60 seconds (auto-logout trigger)
    const autoLogout$ = timer(60000).pipe(map(() => 'autoLogout'));

    // Observables for user actions (button clicks)
    const extendSession$ = this._modalPromptService.$primaryButtonClick.pipe(
      map(() => 'extend'),
    );
    const manualLogout$ = this._modalPromptService.$warningButtonClick.pipe(
      map(() => 'logout'),
    );

    const raceSubscription = race(autoLogout$, extendSession$, manualLogout$)
      .pipe(take(1))
      .subscribe((result) => {
        // Stop the countdown subscription when an event occurs
        countdownSubscription.unsubscribe();

        // Close the modal
        this._modalPromptService.closeModal();

        if (result === 'extend') {
          this._authService.refreshToken().subscribe({
            next: () => {
              this.resetTimer();
            },
            error: (err) => {
              console.error('Token refresh failed', err);
              this._authService.signOut();
            },
          });
        } else {
          this._authService.signOut();
        }
      });
    this.subscriptions.add(raceSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
